<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { numberFormatter, relationLabel } from "@/utils/formatters";
import { store } from "@/store";
import useApi from "@/composables/useApi";
import useInvoiceStatus from "@/composables/useInvoiceStatus";

import AppCard from "@/components/AppCard";
import AppCheckbox from "@/components/Form/AppCheckbox";
import AppAlert from "@/components/AppAlert";
import AppAlertSubscription from "@/components/AppAlertSubscription";
//
import AppSubscriptionForm from "@/components/Payment/AppSubscriptionForm";

const route = useRoute();
const router = useRouter();
const { service, invoice } = useApi();
const checkvalue = ref(false);
const invoiceData = ref(null);
const service_id = ref(null);
const serviceData = ref(null);
const lastInvoiceId = ref(null);
const countInvoicesPending = ref(null);
const is_last_invoice = ref(false);
const subscription_exists = ref(false);
const allowSubscription = ref(false);
const payment_in_process = ref(false);

const { due, formatDate, paymentTerm, residual, stateLabel, status, tags } =
  useInvoiceStatus(invoiceData);

const customer = computed(() => relationLabel(invoiceData.value?.partner_id));
const serviceName = computed(() =>
  relationLabel(invoiceData.value?.servicio_id)
);

const relationId = (value) => (Array.isArray(value) ? value[0] : value);

// The ids come from the URL, so only accept records owned by the logged in
// customer: anyone could type the id of someone else's invoice
const getService = async (id, partner_id) => {
  const data = await service.getServiceById(id, partner_id);
  const found = data.payload?.[0];
  return found && relationId(found.partner_id) === partner_id ? found : null;
};

const getInvoice = async (id, partner_id) => {
  const data = await invoice.getInvoiceById(id, partner_id);
  const found = data.payload?.[0];
  return found && relationId(found.partner_id) === partner_id ? found : null;
};

const getCountInvoicesPendingByService = async (service_id) => {
  const data = await invoice.countInvoicesPendingByService(service_id);
  return data.payload;
};

const getLastInvoiceId = async (id) => {
  const data = await invoice.getLastInvoiceId(id);
  return data.payload[0];
};

// Facturas con un pago registrado (APLICADO / PENDIENTE) en el historial de pago
const getPaymentHistoryInvoices = async (service_id) => {
  const data = await invoice.getPaymentHistoryInvoicesByService(service_id);
  return data;
};

const getAllowSubscription = async () => {
  // Validaciones para suscribir
  if (
    (invoiceData.value.payment_state === "not_paid" ||
      invoiceData.value.payment_state === "partial") &&
    is_last_invoice.value &&
    countInvoicesPending.value === 1 &&
    !["INSTALACION RESIDENCIAL", "PRIMERA MENSUALIDAD"].includes(
      invoiceData.value.tipo_factura_lead
    ) &&
    invoiceData.value.factura_reconexion === false &&
    invoiceData.value.factura_penalizacion === false &&
    invoiceData.value.factura_por_equipo === false &&
    invoiceData.value.factura_por_excedente === false &&
    // Evita doble cargo: no domiciliar si ya existe un pago en proceso
    !payment_in_process.value
  ) {
    return true;
  }
  return false;
};

onMounted(async () => {
  try {
    const { id } = route.params;
    store.APP_LOADING = true;
    store.I118N_TITLE = false;
    store.APP_TITLE = "";
    // Render the heading with its back arrow (the router clears it on every
    // navigation)
    store.APP_BACK_BUTTON = true;
    const invoiceId = Number(id);
    const partnerId = store.user?.id;
    const ownInvoice =
      Number.isInteger(invoiceId) && invoiceId > 0 && partnerId
        ? await getInvoice(invoiceId, partnerId)
        : null;
    const ownService =
      ownInvoice && Array.isArray(ownInvoice.servicio_id)
        ? await getService(ownInvoice.servicio_id[0], partnerId)
        : null;
    if (!ownService) {
      router.replace({ name: "Home" });
      return;
    }
    invoiceData.value = ownInvoice;
    store.APP_TITLE = invoiceData.value.name;
    service_id.value = invoiceData.value.servicio_id[0];
    serviceData.value = ownService;
    lastInvoiceId.value = await getLastInvoiceId(service_id.value);
    if (invoiceData.value.id === lastInvoiceId.value.id) {
      is_last_invoice.value = true;
    }
    countInvoicesPending.value = await getCountInvoicesPendingByService(
      service_id.value
    );
    subscription_exists.value = serviceData.value.subscription_status;
    const invoices_in_payment_history = await getPaymentHistoryInvoices(
      service_id.value
    );
    payment_in_process.value = invoices_in_payment_history.includes(
      invoiceData.value.id
    );
    allowSubscription.value = await getAllowSubscription();
  } catch (error) {
    console.log(error);
  } finally {
    store.APP_LOADING = false;
  }
});
</script>
<template lang="pug">
.invoice-view(v-if="invoiceData")
  AppCard.invoice-view__card(padding="lg" elevated)
    .detail
      header.detail__header
        .detail__heading
          .detail__label {{ $t("invoices.invoice") }}
          h1.detail__folio {{ invoiceData.name }}
        span.detail__status(:class="status.variant") {{ status.label }}
      .detail__amounts
        .detail__amount
          h4 {{ $t("invoices.amount_total") }}
          strong {{ numberFormatter(invoiceData.amount_total) }}
        .detail__amount.detail__amount--right
          h4 {{ $t("invoices.amount_residual") }}
          strong(:class="{ 'is-pending': residual > 0 }") {{ numberFormatter(invoiceData.amount_residual) }}
      .detail__due(:class="due.variant")
        i.uil(:class="due.icon" aria-hidden="true")
        span {{ due.label }}
      .detail__grid
        .detail__item
          h4 {{ $t("invoices.partner_id") }}
          span(:title="customer") {{ customer }}
        .detail__item
          h4 {{ $t("invoices.servicio_id") }}
          span {{ serviceName }}
        .detail__item
          h4 {{ $t("invoices.invoice_date") }}
          span {{ formatDate(invoiceData.invoice_date) }}
        .detail__item
          h4 {{ $t("invoices.due_date") }}
          span {{ formatDate(invoiceData.invoice_date_due) }}
        .detail__item
          h4 {{ $t("invoices.amount_untaxed") }}
          span {{ numberFormatter(invoiceData.amount_untaxed) }}
        .detail__item
          h4 {{ $t("invoices.amount_tax") }}
          span {{ numberFormatter(invoiceData.amount_tax) }}
        .detail__item
          h4 {{ $t("invoices.invoice_payment_term_id") }}
          span(:title="paymentTerm") {{ paymentTerm }}
        .detail__item
          h4 {{ $t("invoices.state") }}
          span {{ stateLabel }}
      .detail__tags(v-if="tags.length")
        span.detail__tag(v-for="tag in tags" :key="tag.key" :class="tag.variant") {{ tag.label }}

  AppCard.invoice-view__subscription(padding="lg")
    h2.invoice-view__title
      i.uil.uil-sync(aria-hidden="true")
      span {{ $t("invoices.subscription") }}
    AppAlert(
      v-if="subscription_exists"
      :title="$t('payment.subscription.title')"
      :subtitle="$t('payment.subscription.subtitle')"
      type="info"
    )
    AppAlert(
      v-else-if="payment_in_process"
      :title="$t('payment.subscription_in_process.title')"
      :subtitle="$t('payment.subscription_in_process.subtitle')"
      type="info"
    )
    template(v-else-if="allowSubscription")
      AppAlert.mb-4(
        :title="$t('invoices.message_info_confirm')"
        :subtitle="$t('invoices.message_header')"
        type="info"
      )
      .invoice-view__consent
        AppCheckbox(v-model="checkvalue" :label="$t('invoices.confirm')")
      AppSubscriptionForm(
        :invoice="invoiceData"
        :service="serviceData"
        :name="invoiceData.name"
        :disabled="!checkvalue"
      )
    AppAlertSubscription(
      v-else
      :title="$t('payment.subscription_disabled.title')"
      :subtitle="$t('payment.subscription_disabled.subtitle')"
    )
</template>

<style lang="scss" scoped>
.invoice-view {
  @apply pb-6;
  @apply max-w-4xl;

  &__card {
    @apply mb-6;
  }

  &__title {
    @apply flex;
    @apply items-center;
    @apply gap-2;
    @apply mb-4;
    @apply font-semibold;
    @apply text-bluegray-600;

    i {
      @apply text-xl;
      @apply leading-none;
      @apply text-gignetgreen-600;
    }
  }

  &__consent {
    @apply p-3;
    @apply mb-4;
    @apply rounded-lg;
    @apply bg-bluegray-50;
    @apply border;
    @apply border-bluegray-100;
  }
}

.detail {
  &__header {
    @apply flex;
    @apply items-start;
    @apply justify-between;
    @apply gap-3;
  }

  &__heading {
    @apply min-w-0;
  }

  &__label {
    @apply text-xs;
    @apply text-bluegray-400;
    @apply uppercase;
    @apply tracking-wide;
  }

  &__folio {
    @apply text-xl;
    @apply text-bluegray-700;
    @apply font-semibold;
    @apply leading-snug;
    @apply truncate;
  }

  &__status {
    @apply flex-shrink-0;
    @apply px-3;
    @apply py-1.5;
    @apply rounded-full;
    @apply text-xs;
    @apply font-semibold;
    @apply leading-none;
    @apply whitespace-nowrap;

    &.success {
      @apply bg-gignetgreen-200;
      @apply text-gignetgreen-800;
    }
    &.info {
      @apply bg-gignetblue-100;
      @apply text-gignetblue-600;
    }
    &.warning {
      @apply bg-yellow-100;
      @apply text-yellow-700;
    }
    &.danger {
      @apply bg-red-100;
      @apply text-red-600;
    }
    &.neutral {
      @apply bg-bluegray-100;
      @apply text-bluegray-500;
    }
  }

  &__amounts {
    @apply flex;
    @apply items-end;
    @apply justify-between;
    @apply gap-4;
    @apply mt-5;
    @apply pb-5;
    @apply border-b;
    @apply border-bluegray-100;

    h4 {
      @apply text-xs;
      @apply font-medium;
      @apply text-bluegray-400;
      @apply uppercase;
      @apply tracking-wide;
    }

    strong {
      @apply block;
      @apply text-2xl;
      @apply font-semibold;
      @apply text-bluegray-700;
      @apply leading-tight;
      font-variant-numeric: tabular-nums;

      &.is-pending {
        @apply text-red-500;
      }

      @screen sm {
        @apply text-3xl;
      }
    }
  }

  &__amount--right {
    @apply text-right;

    strong {
      @apply text-xl;
      @screen sm {
        @apply text-2xl;
      }
    }
  }

  &__due {
    @apply flex;
    @apply items-center;
    @apply gap-1.5;
    @apply mt-4;
    @apply text-sm;
    @apply text-bluegray-500;

    i {
      @apply text-lg;
      @apply leading-none;
      @apply text-bluegray-300;
    }

    &.is-overdue {
      @apply text-red-500;
      @apply font-medium;
      i {
        @apply text-red-400;
      }
    }

    &.is-paid {
      @apply text-gignetgreen-700;
      i {
        @apply text-gignetgreen-500;
      }
    }
  }

  &__grid {
    @apply grid;
    @apply grid-cols-2;
    @apply gap-x-4;
    @apply gap-y-4;
    @apply mt-5;
    @apply pt-5;
    @apply border-t;
    @apply border-bluegray-100;
    @screen lg {
      @apply grid-cols-4;
    }
  }

  &__item {
    @apply min-w-0;

    h4 {
      @apply text-xs;
      @apply font-medium;
      @apply text-bluegray-400;
      @apply uppercase;
      @apply tracking-wide;
      @apply truncate;
    }

    span {
      @apply block;
      @apply text-sm;
      @apply text-bluegray-700;
      @apply truncate;
    }
  }

  &__tags {
    @apply flex;
    @apply flex-wrap;
    @apply gap-2;
    @apply mt-5;
  }

  &__tag {
    @apply px-2;
    @apply py-1;
    @apply rounded-md;
    @apply bg-bluegray-100;
    @apply text-bluegray-600;
    @apply text-xs;
    @apply font-medium;
    @apply leading-none;

    &.danger {
      @apply bg-red-50;
      @apply text-red-500;
    }
  }
}
</style>
